import { useEffect, useState, useCallback } from 'react'
import { adminAPI } from '../../lib/api'
import type { Announcement, User } from '../../types'
import { Input } from '../../components/ui/Input'
import { Textarea } from '../../components/ui/Textarea'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { useToast } from '../../components/ui/useToast'
import {
  Bell, Send, Trash2, AlertCircle, CheckCircle, Building2,
  Globe, MessageSquare, Calendar, RefreshCw, Plus, Filter
} from 'lucide-react'

export function AdminAnnouncementsPage() {
  const [user, setUser] = useState<User | null>(null)
  const [messages, setMessages] = useState<Announcement[]>([])
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [filterScope, setFilterScope] = useState('all')
  const [loading, setLoading] = useState(false)
  const { notify } = useToast()

  useEffect(() => {
    ;(async () => {
      try {
        const sessionRes = await fetch('/api/admin/session', { credentials: 'include' })
        if (sessionRes.ok) {
          const sessionData = await sessionRes.json()
          if (sessionData.success && sessionData.data?.user) {
            setUser(sessionData.data.user)
          }
        }
      } catch {
        // Silent fail
      }
    })()
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    const res = await adminAPI.messagesList()
    if (res.success) setMessages(Array.isArray(res.data) ? (res.data as Announcement[]) : [])
    else notify({ kind: 'error', message: res.message || 'Failed to load messages' })
    setLoading(false)
  }, [notify])

  useEffect(() => { load() }, [load])

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      notify({ kind: 'error', message: 'Title and body are required' })
      return
    }
    const payload: { title: string; body: string; tenant_id?: number } = {
      title: title.trim(),
      body: body.trim()
    }
    if (tenantId) payload.tenant_id = parseInt(tenantId, 10)

    setLoading(true)
    const res = await adminAPI.messagesCreate(payload)
    setLoading(false)

    if (res.success) {
      notify({ kind: 'success', message: 'Announcement sent successfully' })
      setTitle('')
      setBody('')
      setTenantId('')
      load()
    } else {
      notify({ kind: 'error', message: res.message || 'Failed to send announcement' })
    }
  }

  const filteredMessages = messages.filter(m => {
    if (filterScope === 'global') return !m.tenant_id
    if (filterScope === 'tenant') return !!m.tenant_id
    return true
  })

  if (user?.role !== 'SuperAdmin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Global Announcements
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Send announcements globally or to specific tenants
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          disabled={loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5 border-blue-200 dark:border-blue-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Announcements</p>
                <p className="text-3xl font-bold mt-2 text-blue-900 dark:text-blue-100">{messages.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/5 dark:to-pink-500/5 border-purple-200 dark:border-purple-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Global Messages</p>
                <p className="text-3xl font-bold mt-2 text-purple-900 dark:text-purple-100">
                  {messages.filter(m => !m.tenant_id).length}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                <Globe className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/5 dark:to-red-500/5 border-orange-200 dark:border-orange-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Tenant Messages</p>
                <p className="text-3xl font-bold mt-2 text-orange-900 dark:text-orange-100">
                  {messages.filter(m => !!m.tenant_id).length}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-500">
                <Building2 className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Announcement */}
      <div className="card border-2 border-orange-200 dark:border-orange-800">
        <div className="card-header flex items-center gap-2 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10">
          <Plus className="h-5 w-5 text-orange-600" />
          <h2 className="font-semibold text-lg">Create New Announcement</h2>
        </div>
        <div className="card-body">
          <div className="grid gap-4">
            <div>
              <label className="form-label flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-500" />
                Title *
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Announcement title..."
                className="text-lg font-medium"
              />
            </div>
            <div>
              <label className="form-label flex items-center gap-2">
                <Bell className="h-4 w-4 text-gray-500" />
                Message *
              </label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Type your announcement message here..."
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">
                Message will be displayed to users in their dashboard
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  Target Audience
                </label>
                <Select value={tenantId ? 'tenant' : 'global'} onChange={(e) => {
                  if (e.target.value === 'global') setTenantId('')
                }}>
                  <option value="global">All Tenants (Global)</option>
                  <option value="tenant">Specific Tenant</option>
                </Select>
              </div>
              {tenantId !== '' && (
                <div>
                  <label className="form-label">Tenant ID</label>
                  <Input
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    placeholder="Enter tenant ID"
                    type="number"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
              <Button
                variant="outline"
                onClick={() => {
                  setTitle('')
                  setBody('')
                  setTenantId('')
                }}
                disabled={loading}
              >
                Clear
              </Button>
              <Button
                onClick={send}
                disabled={loading || !title.trim() || !body.trim()}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {loading ? 'Sending...' : 'Send Announcement'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            <h2 className="font-semibold text-lg">Recent Announcements</h2>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <Select value={filterScope} onChange={(e) => setFilterScope(e.target.value)} className="text-sm">
              <option value="all">All Messages</option>
              <option value="global">Global Only</option>
              <option value="tenant">Tenant-Specific</option>
            </Select>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl animate-pulse">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Announcements</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Create your first announcement to communicate with tenants
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMessages.map(m => (
                <div
                  key={m.id}
                  className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${
                        m.tenant_id
                          ? 'bg-orange-100 dark:bg-orange-500/10'
                          : 'bg-purple-100 dark:bg-purple-500/10'
                      }`}>
                        {m.tenant_id ? (
                          <Building2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        ) : (
                          <Globe className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{m.title}</h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            m.tenant_id
                              ? 'bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300'
                              : 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300'
                          }`}>
                            {m.tenant_id ? `Tenant #${m.tenant_id}` : 'Global'}
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-2">{m.body}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(m.created_at).toLocaleString()}
                          </div>
                          {m.tenant_id && (
                            <div className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              Tenant: {m.tenant_id}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                      onClick={() => {
                        if (confirm('Delete this announcement?')) {
                          notify({ kind: 'info', message: 'Delete functionality coming soon' })
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-xl">
        <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
        <div className="flex-1 text-sm">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Announcement Guidelines</h3>
          <p className="text-blue-800 dark:text-blue-300">
            Global announcements are visible to all tenants and users across the platform.
            Tenant-specific announcements are only visible to users within that specific tenant organization.
            Use announcements to communicate platform updates, maintenance schedules, or important notices.
          </p>
        </div>
      </div>
    </div>
  )
}
